import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { addPages, removePage } from '../../../redux/reducers/navigationReducer/NavigationSlice';
import { NavigationKey } from '../../../components/appNavigationBar/AppNavigation.types';
import PageHeader from '../../../components/common/pageHeader/PageHeader';
import CustomTabs from '../../../components/common/tabs/CustomTabs';
import { getLiveGuidelineByIdAction } from '../../../redux/actions/liveGuidelineActions';
import { markLiveGuidelineProductsChanged } from '../../../redux/reducers/liveGuidelineReducer/LiveGuidelineSlice';
import { LiveGuidelineDetail as LiveGuidelineDetailType } from '../../../redux/actions/liveGuidelineActions/types';
import { ActionResponse } from '../../../models/actionResponse/types';
import ProductsTab from './tabs/ProductsTab/ProductsTab';
import GuidelineInformationTab from './tabs/GuidelineInformationTab/GuidelineInformationTab';
import ReportingTab from './tabs/ReportingTab/ReportingTab';
import NotificationsTab from './tabs/NotificationsTab/NotificationsTab';
import { LiveGuidelineDetailTabContainer } from './LiveGuidelineDetail.styles';
import LiveGuidelineDetailActions from './LiveGuidelineDetailActions';
import { getAccessToken } from '../../../redux/persistedState';
import { usePermissions } from '../../../hooks/permissions';
import { AccountPermissionTypes } from '../../../models/permission/enums';

interface SyncEventHandlers {
    onInProgress: () => void;
    onCompleted: () => void;
}

const getSyncEventData = (event: string): string | undefined =>
    event
        .split(/\r?\n/)
        .find((line) => line.startsWith('data: '))
        ?.slice('data: '.length);

const listenForSyncEvents = async (
    liveGuidelineId: number,
    signal: AbortSignal,
    handlers: SyncEventHandlers,
) => {
    const response = await fetch(
        `${process.env.REACT_APP_API_URL}/live-guidelines/${liveGuidelineId}/sync-events`,
        {
            headers: { authorization: `bearer ${getAccessToken()}` },
            signal,
        },
    );
    if (!response.ok || !response.body) {
        throw new Error(`Unable to listen for guideline sync events (${response.status})`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
        const { done, value } = await reader.read();
        if (done) {
            return;
        }

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split(/\r?\n\r?\n/);
        buffer = events.pop() || '';
        for (const event of events) {
            const data = getSyncEventData(event);
            if (data === 'in-progress') {
                handlers.onInProgress();
            } else if (data === 'completed') {
                handlers.onCompleted();
                return;
            }
        }
    }
};

interface Params {
    id: string;
    tab?: string;
}

const LiveGuidelineDetail: React.FC = () => {
    const params = useParams<Params>();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { authReducer, liveGuidelineReducer } = useAppSelector((state) => state);
    const { canManageGuidelines } = usePermissions();

    const [guideline, setGuideline] = useState<LiveGuidelineDetailType | null>(null);
    const [loadedGuidelineId, setLoadedGuidelineId] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState(params?.tab || '1');
    const [isSyncInProgress, setIsSyncInProgress] = useState(false)
    const [canManageOwners, setCanManageOwners] = useState(false);

    const liveGuidelineId = Number(params.id);
    // Set when the user came from a Project's "Add from a Live Guideline"; holds the target project id.
    const copyProductsTo = new URLSearchParams(location.search).get('copyProductsTo') ?? undefined;

    useEffect(() => {
        // An Account Admin manages any guideline; a Member needs the privilege and must own it.
        const isAccountAdmin = authReducer.user?.accountRole === AccountPermissionTypes.ACCOUNT_ADMIN;
        const isOwner = !!guideline?.owners.some((owner) => owner.userId === authReducer.user?.id);
        setCanManageOwners(isAccountAdmin || (canManageGuidelines() && isOwner));
    }, [canManageGuidelines, guideline?.owners, authReducer.user?.id, authReducer.user?.accountRole]);

    useEffect(() => {
        getGuideline();
    }, [liveGuidelineId]);

    useEffect(() => {
        const abortController = new AbortController();

        void listenForSyncEvents(liveGuidelineId, abortController.signal, {
            onInProgress: () => setIsSyncInProgress(true),
            onCompleted: () => {
                setIsSyncInProgress(false);
                // Soft refresh: reuses the same signal ProductsTab/NotificationsTab already watch for
                // hide/restore updates, so products/count refetch in place instead of a full page reload.
                dispatch(markLiveGuidelineProductsChanged());
            },
        }).catch((error: unknown) => {
            if (!abortController.signal.aborted) {
                console.error('Live Guideline sync event stream failed', error);
            }
        });

        return () => abortController.abort();
    }, [liveGuidelineId]);

    useEffect(() => {
        // Refresh the bootstrap payload (e.g. productsCount) after a hide/restore action elsewhere.
        if (guideline?.id && liveGuidelineReducer.productsChangedAt > 0) {
            getGuideline();
        }
    }, [liveGuidelineReducer.productsChangedAt]);

    useEffect(() => {
        dispatch(
            addPages([
                {
                    key: NavigationKey.DASHBOARD,
                    value: `${authReducer.user?.firstName}’s Dashboard`,
                },
                {
                    key: NavigationKey.LIVE_GUIDELINES,
                    value: 'Live Guidelines',
                },
                {
                    key: `/live-guidelines/${liveGuidelineId}`,
                    value: guideline?.name || '',
                    image: 'src/assets/images/lightning-charge-fill.svg',
                },
            ]),
        );
        return () => {
            dispatch(removePage());
            dispatch(removePage());
        };
    }, [guideline?.name]);

    const getGuideline = async () => {
        if (!liveGuidelineId) {
            return;
        }
        try {
            const { payload } = (await dispatch(
                getLiveGuidelineByIdAction(liveGuidelineId),
            )) as ActionResponse<LiveGuidelineDetailType>;
            setGuideline(payload?.data || null);
        } finally {
            setLoadedGuidelineId(liveGuidelineId);
        }
    };

    const trackActiveTab = (tab: string) => {
        setActiveTab(tab);
        window.history.replaceState(null, '', `/live-guidelines/${liveGuidelineId}/${tab}`);
    };

    const tabs = [
        {
            name: `Products (${guideline?.productsCount || 0})`,
            tab: (
                <ProductsTab
                    liveGuidelineId={liveGuidelineId}
                    liveGuidelineName={guideline?.name || ''}
                    canHideProducts={
                        guideline?.owners.some((owner) => owner.userId === authReducer.user?.id) ||
                        authReducer.user?.accountRole === AccountPermissionTypes.ACCOUNT_ADMIN
                    }
                    guidelineCriteria={guideline?.criteria ?? []}
                    isGuidelineLoading={loadedGuidelineId !== liveGuidelineId}
                    isSyncInProgress={isSyncInProgress}
                    copyProductsTo={copyProductsTo}
                    onNavigateToNotifications={() => trackActiveTab('4')}
                    canManageGuidelines={canManageGuidelines}
                />
            ),
            key: '1',
        },
        {
            name: 'Live Guideline information',
            tab: <GuidelineInformationTab guideline={guideline} reload={getGuideline} canManageOwners={canManageOwners} />,
            key: '2',
        },
        {
            name: 'Reporting',
            tab: <ReportingTab guidelineInfo={{ name: guideline?.name, id: guideline?.id }} />,
            key: '3',
        },
        {
            name: 'Notifications',
            tab: (
                <NotificationsTab
                    liveGuidelineId={liveGuidelineId}
                    liveGuidelineName={guideline?.name || ''}
                />
            ),
            key: '4',
        },
    ];

    return (
        <>
            <PageHeader
                title={guideline?.name || ''}
                iconTitleGap={4}
                iconProp={
                guideline?.name
                        ? {
                              src: 'lightning-charge-fill.svg',
                              style: { width: 24, height: 24 },
                          }
                        : undefined
                }
            >
                {guideline && (
                    <LiveGuidelineDetailActions key={guideline.id} guideline={guideline} />
                )}
            </PageHeader>
            <LiveGuidelineDetailTabContainer>
                <CustomTabs
                    tabs={tabs}
                    defaultKey={params?.tab || '1'}
                    activeKey={activeTab}
                    onTabChange={trackActiveTab}
                />
            </LiveGuidelineDetailTabContainer>
        </>
    );
};

export default LiveGuidelineDetail;
